import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { FiShield, FiAlertTriangle, FiRefreshCw, FiTrash2, FiSearch } from 'react-icons/fi';
import { getAuditLogs, getSuspiciousIps, cleanupAudit } from '../../services/securityService';
import { fmtTime } from '../../utils/format';

// 事件类型筛选选项(与后端 EVENT_NAMES 对齐)
const EVENT_OPTIONS = [
  { value: '', label: '全部事件' },
  { value: 'push_reject', label: '推送被拒' },
  { value: 'push_ok', label: '推送成功' },
  { value: 'verify', label: '授权验证' },
  { value: 'poll_reject', label: '拉指令被拒' }
];

const TIME_RANGES = [
  { value: 6, label: '最近6小时' },
  { value: 24, label: '最近24小时' },
  { value: 72, label: '最近3天' },
  { value: 168, label: '最近7天' }
];

// 事件徽章配色
const EVENT_STYLE = {
  push_reject: { cls: 'bg-[#ffebe9] text-[#cf222e]', name: '推送被拒' },
  push_ok: { cls: 'bg-[#dafbe1] text-[#1a7f37]', name: '推送成功' },
  verify: { cls: 'bg-[#ddf4ff] text-[#0969da]', name: '授权验证' },
  poll_reject: { cls: 'bg-[#ffebe9] text-[#cf222e]', name: '拉指令被拒' },
  login_fail: { cls: 'bg-[#fff8c5] text-[#9a6700]', name: '登录失败' }
};

const SecurityAudit = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(1);
  const [eventType, setEventType] = React.useState('');
  const [ip, setIp] = React.useState('');
  const [identity, setIdentity] = React.useState('');
  const [hours, setHours] = React.useState(24);
  // 输入框草稿(点搜索才应用, 防每敲一个字符都重新请求)
  const [ipDraft, setIpDraft] = React.useState('');
  const [identityDraft, setIdentityDraft] = React.useState('');

  // 可疑IP分析(24小时窗口)
  const { data: suspiciousData, isLoading: suspiciousLoading } = useQuery(
    ['securitySuspicious', 24],
    () => getSuspiciousIps(24),
    { refetchInterval: 60000 }
  );
  const suspicious = suspiciousData?.suspicious || [];

  // 审计日志分页
  const { data: logsData, isLoading } = useQuery(
    ['securityAudit', page, eventType, ip, identity, hours],
    () => getAuditLogs({ page, limit: 30, eventType, ip, identity, hours }),
    { refetchInterval: 30000, keepPreviousData: true }
  );
  const logs = logsData?.logs || [];
  const pagination = logsData?.pagination || { page: 1, pages: 1, total: 0 };

  // 清理突变
  const cleanupMutation = useMutation(() => cleanupAudit(30), {
    onSuccess: (data) => {
      toast.success(data.message || '清理完成');
      queryClient.invalidateQueries('securityAudit');
    },
    onError: (err) => toast.error(err.message || '清理失败')
  });

  const applySearch = () => {
    setIp(ipDraft.trim());
    setIdentity(identityDraft.trim());
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">安全审计</h1>
          <p className="text-[#656d76] text-sm mt-1">推送 / 验证 / 拉指令的 IP 溯源：谁在动你的云端信号，从这里看</p>
        </div>
        <button
          onClick={() => {
            if (window.confirm('确认清理 30 天前的审计日志？近期记录不受影响。')) {
              cleanupMutation.mutate();
            }
          }}
          disabled={cleanupMutation.isLoading}
          className="flex items-center gap-1 text-sm text-[#57606a] border border-[#d0d7de] rounded-md px-3 py-1.5 bg-white hover:bg-[#f6f8fa] disabled:opacity-50"
        >
          <FiTrash2 /> 清理30天前
        </button>
      </div>

      {/* 可疑IP聚合(重点区) */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm">
        <div className="px-5 py-3 border-b border-[#d0d7de] flex items-center gap-2">
          <FiAlertTriangle className="text-[#cf222e]" />
          <h2 className="text-[#1f2328] font-medium">可疑 IP（最近24小时）</h2>
          <span className="text-xs text-[#8b949e]">同一 IP 命中多个凭证 = 凭证泄露/盗用铁证；拒绝密集 = 爆破指纹</span>
        </div>
        {suspiciousLoading ? (
          <div className="px-5 py-8 text-center text-[#8b949e] text-sm">分析中...</div>
        ) : suspicious.length === 0 ? (
          <div className="px-5 py-8 text-center text-[#1a7f37] text-sm">
            未发现可疑 IP：没有同一 IP 动多个凭证、也没有拒绝密集的源
          </div>
        ) : (
          <div className="divide-y divide-[#eaeef2]">
            {suspicious.map(s => (
              <div key={s.ip} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-[#cf222e] font-medium">{s.ip}</span>
                    {s.tags.map(t => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#ffebe9] text-[#cf222e]">{t}</span>
                    ))}
                  </div>
                  <div className="text-[#656d76] text-xs mt-0.5">
                    命中 {s.hits} 次 · 涉及 {s.distinctKeys} 个凭证 · 最近 {fmtTime(s.lastSeen)}
                  </div>
                </div>
                <button
                  onClick={() => { setIpDraft(s.ip); setIp(s.ip); setPage(1); }}
                  className="shrink-0 flex items-center gap-1 text-xs text-[#0969da] border border-[#d0d7de] bg-white rounded-md px-2.5 py-1.5 hover:bg-[#f6f8fa]"
                >
                  <FiSearch /> 查明细
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 筛选条 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-[#57606a] mb-1">事件类型</label>
            <select
              value={eventType}
              onChange={(e) => { setEventType(e.target.value); setPage(1); }}
              className="w-full border border-[#d0d7de] rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            >
              {EVENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-[#57606a] mb-1">IP 包含</label>
            <input
              value={ipDraft}
              onChange={(e) => setIpDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder="如 192.168"
              className="w-full border border-[#d0d7de] rounded-md px-2.5 py-1.5 text-sm font-mono bg-white focus:outline-none focus:border-[#0969da]"
            />
          </div>
          <div>
            <label className="block text-xs text-[#57606a] mb-1">凭证前缀（脱敏前10位）</label>
            <input
              value={identityDraft}
              onChange={(e) => setIdentityDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder="如 LIC-AB"
              className="w-full border border-[#d0d7de] rounded-md px-2.5 py-1.5 text-sm font-mono bg-white focus:outline-none focus:border-[#0969da]"
            />
          </div>
          <div className="flex items-end gap-2">
            <select
              value={hours}
              onChange={(e) => { setHours(Number(e.target.value)); setPage(1); }}
              className="flex-1 border border-[#d0d7de] rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            >
              {TIME_RANGES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button
              onClick={applySearch}
              className="flex items-center gap-1 text-sm text-white bg-[#1f2328] rounded-md px-3 py-1.5 hover:bg-[#32383f]"
            >
              <FiSearch /> 筛选
            </button>
          </div>
        </div>
      </div>

      {/* 审计日志表格 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm">
        <div className="px-5 py-3 border-b border-[#d0d7de] flex items-center justify-between">
          <h2 className="text-[#1f2328] font-medium flex items-center gap-2">
            <FiShield /> 审计日志 <span className="text-xs text-[#8b949e]">共 {pagination.total} 条</span>
          </h2>
          <button
            onClick={() => queryClient.invalidateQueries('securityAudit')}
            className="flex items-center gap-1 text-xs text-[#57606a] hover:text-[#1f2328]"
          >
            <FiRefreshCw /> 刷新
          </button>
        </div>
        {isLoading ? (
          <div className="px-5 py-10 text-center text-[#8b949e] text-sm">加载中...</div>
        ) : logs.length === 0 ? (
          <div className="px-5 py-10 text-center text-[#656d76] text-sm">当前筛选条件下没有记录</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#8b949e] border-b border-[#eaeef2]">
                  <th className="px-5 py-2 font-medium">时间</th>
                  <th className="px-3 py-2 font-medium">事件</th>
                  <th className="px-3 py-2 font-medium">凭证（脱敏）</th>
                  <th className="px-3 py-2 font-medium">IP</th>
                  <th className="px-3 py-2 font-medium">客户端</th>
                  <th className="px-3 py-2 font-medium">详情</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eaeef2]">
                {logs.map(l => {
                  const style = EVENT_STYLE[l.eventType] || { cls: 'bg-[#f6f8fa] text-[#57606a]', name: l.eventType };
                  return (
                    <tr key={l.id} className="hover:bg-[#f6f8fa]">
                      <td className="px-5 py-2.5 text-[#57606a] text-xs whitespace-nowrap">{fmtTime(l.createdAt)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded ${style.cls}`}>{style.name}</span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-[#1f2328]">
                        <span className="text-[#8b949e]">{l.identityType === 'source' ? '源' : '码'}</span> {l.identityValue}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-[#0969da]">{l.ip || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-[#8b949e] max-w-[180px] truncate" title={l.userAgent || ''}>
                        {l.userAgent || '-'}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-[#57606a] max-w-[220px] truncate" title={l.detail || ''}>
                        {l.detail || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* 分页 */}
        {pagination.pages > 1 && (
          <div className="px-5 py-3 border-t border-[#eaeef2] flex items-center justify-between text-xs text-[#57606a]">
            <span>第 {pagination.page} / {pagination.pages} 页</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
                className="px-2.5 py-1 border border-[#d0d7de] rounded bg-white hover:bg-[#f6f8fa] disabled:opacity-40"
              >
                上一页
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={pagination.page >= pagination.pages}
                className="px-2.5 py-1 border border-[#d0d7de] rounded bg-white hover:bg-[#f6f8fa] disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 说明 */}
      <p className="text-xs text-[#8b949e]">
        说明：凭证按脱敏存储（前10位），审计表本身不构成泄露源；EA 每 5 分钟验证一次即产生一条 verify 记录，
        可用「清理30天前」控制表体积。VPS 套了 Nginx 反代时取 X-Forwarded-For 首段，直连取连接地址。
      </p>
    </div>
  );
};

export default SecurityAudit;
